import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { apiService } from '../services/api';
import { cacheService } from '../services/cacheService';

interface User {
  id: string;
  username: string;
  email: string;
  role: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (identifier: string, credential: string, method: 'credentials' | 'pin') => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: User | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const checkAuth = useCallback(async () => {
    if (initialized) return;

    try {
      const token = localStorage.getItem('auth_token');
      const userData = localStorage.getItem('user_data');
      
      if (!token || !userData) {
        console.log('🔍 No token or user data found, user not authenticated');
        setUser(null);
        setLoading(false);
        setInitialized(true);
        return;
      }

      // Set user from localStorage immediately for better UX
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        console.log('👤 User loaded from localStorage:', parsedUser);
        
        // Set up token refresh immediately
        apiService.setupTokenRefresh();
      } catch (e) {
        console.error('Error parsing user data:', e);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_data');
        setUser(null);
        setLoading(false);
        setInitialized(true);
        return;
      }

      // Verify token with server in the background
      const verifyToken = async () => {
        try {
          console.log('🔑 Verifying token with server...');
          console.log('🔍 Current token:', localStorage.getItem('auth_token')?.substring(0, 20) + '...');
          
          const isValid = await apiService.validateToken();
          console.log('🔑 Token validation result:', isValid);
          
          if (!isValid) {
            throw new Error('Token validation failed');
          }
          console.log('✅ Token is valid, getting fresh user data...');
          
          // If we get here, token is valid - get fresh user data
          const user = await apiService.checkAuth();
          console.log('🔍 Fresh user data received:', user);
          
          if (user) {
            localStorage.setItem('user_data', JSON.stringify(user));
            setUser(user);
            console.log('✅ User data refreshed successfully');
          } else {
            throw new Error('Failed to get user data');
          }
        } catch (error) {
          console.error('❌ Token validation failed:', error);
          console.error('❌ Error details:', {
            message: error instanceof Error ? error.message : 'Unknown error',
            stack: error instanceof Error ? error.stack : undefined,
            name: error instanceof Error ? error.name : undefined
          });
          
          // Check if this is a real authentication failure
          const isAuthFailure = error instanceof Error && (
            error.message.includes('Token validation failed') ||
            error.message.includes('Invalid token') ||
            error.message.includes('401') ||
            error.message.includes('Unauthorized') ||
            error.message.includes('Token not provided') ||
            error.message.includes('User not found')
          );
          
          // Check if this is a network error
          const isNetworkError = error instanceof Error && (
            error.message.includes('Network Error') ||
            error.message.includes('ERR_NETWORK') ||
            error.message.includes('ECONNREFUSED') ||
            error.message.includes('timeout')
          );
          
          if (isAuthFailure && !isNetworkError) {
            console.log('🔑 Authentication failed, logging out user');
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user_data');
            setUser(null);
            toast.error('Your session has expired. Please log in again.');
            navigate('/login', { replace: true });
          } else {
            console.log('⚠️ Token validation failed due to network/other issue, keeping user logged in');
            console.log('⚠️ Error type:', typeof error);
            console.log('⚠️ Error message:', error instanceof Error ? error.message : String(error));
            console.log('⚠️ Is auth failure:', isAuthFailure);
            console.log('⚠️ Is network error:', isNetworkError);
          }
        }
      };

      // Don't await the verification, let it happen in the background
      verifyToken();
      
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, [initialized, navigate]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (identifier: string, credential: string, method: 'credentials' | 'pin') => {
    try {
      setLoading(true);
      setError(null);
      
      let auth;
      if (method === 'pin') {
        auth = await apiService.loginWithPin(identifier, credential);
      } else {
        auth = await apiService.login(identifier, credential);
      }
      
      setUser(auth.user);
      localStorage.setItem('auth_token', auth.token);
      localStorage.setItem('user_data', JSON.stringify(auth.user));
      toast.success('Login successful');
      
      // Get the redirect path from location state or default to dashboard
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    } catch (err) {
      setError(method === 'pin' ? 'Invalid PIN' : 'Invalid credentials');
      toast.error(method === 'pin' ? 'Invalid PIN' : 'Invalid credentials');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
      await cacheService.clearAllCache();
      setUser(null);
      toast.success('Logout successful');
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout failed:', error);
      setError('Failed to logout');
      toast.error('Logout failed');
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    setUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export { AuthContext, useAuth }; 